import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Download, FileDown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend,
} from "recharts";
import { getBills, getCustomers } from "@/lib/api";
import { exportCSV, exportPDF } from "@/lib/exportUtils";

function ExportButtons({ pdfFn, csvFn }) {
  return (
    <div className="flex gap-2">
      <Button data-testid="export-pdf-btn" variant="outline" size="sm" onClick={pdfFn} className="rounded-sm text-xs">
        <Download className="w-3 h-3 mr-1" /> PDF
      </Button>
      <Button data-testid="export-csv-btn" variant="outline" size="sm" onClick={csvFn} className="rounded-sm text-xs">
        <FileDown className="w-3 h-3 mr-1" /> CSV
      </Button>
    </div>
  );
}

function DateFilter({ dateFrom, setDateFrom, dateTo, setDateTo }) {
  return (
    <div className="flex flex-wrap gap-4 mb-4">
      <div>
        <Label className="text-xs">From</Label>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1 w-auto" data-testid="date-from-input" />
      </div>
      <div>
        <Label className="text-xs">To</Label>
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1 w-auto" data-testid="date-to-input" />
      </div>
    </div>
  );
}

const chartTooltipStyle = { background: "#18181b", border: "none", borderRadius: 0, color: "#fff", fontSize: 11 };

export default function Reports() {
  const [bills, setBills] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split("T")[0]);
  const [rateChangeDate, setRateChangeDate] = useState("");

  useEffect(() => {
    const load = async () => {
      setBills(await getBills());
      setCustomers(await getCustomers());
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      setDateFrom(from.toISOString().split("T")[0]);
      setDateTo(now.toISOString().split("T")[0]);
    };
    load();
  }, []);

  const filteredBills = useMemo(() => {
    if (!dateFrom && !dateTo) return bills;
    return bills.filter((b) => {
      const d = new Date(b.billDate);
      if (dateFrom && d < new Date(dateFrom)) return false;
      if (dateTo && d > new Date(dateTo)) return false;
      return true;
    });
  }, [bills, dateFrom, dateTo]);

  // Report 1: Monthly Collection
  const monthlyData = useMemo(() => {
    const months = {};
    filteredBills.forEach((b) => {
      const d = new Date(b.billDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!months[key]) months[key] = { month: key, billed: 0, collected: 0, pending: 0 };
      months[key].billed += b.netPayable || 0;
      if (b.isPaid) months[key].collected += b.netPayable || 0;
      else months[key].pending += b.netPayable || 0;
    });
    return Object.values(months).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredBills]);

  // Report 2: Customer Consumption
  const consumptionData = useMemo(() => {
    const data = {};
    filteredBills.forEach((b) => {
      if (!data[b.customerId]) data[b.customerId] = { name: b.customerName, consumerNo: b.consumerNumber, totalUnits: 0, totalAmount: 0 };
      data[b.customerId].totalUnits += b.consumedUnits || 0;
      data[b.customerId].totalAmount += b.netPayable || 0;
    });
    return Object.values(data).sort((a, b) => b.totalUnits - a.totalUnits);
  }, [filteredBills]);

  // Report 3: Outstanding Dues
  const outstandingData = useMemo(() => {
    const now = new Date();
    return bills
      .filter((b) => !b.isPaid)
      .map((b) => ({ ...b, daysOverdue: Math.max(0, Math.floor((now - new Date(b.dueDate)) / 86400000)) }))
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
  }, [bills]);

  // Report 4: Society-wise
  const societyData = useMemo(() => {
    const data = {};
    customers.forEach((c) => {
      const s = c.society || "Unassigned";
      if (!data[s]) data[s] = { society: s, customers: 0, billed: 0, collected: 0 };
      data[s].customers++;
    });
    filteredBills.forEach((b) => {
      const s = b.society || "Unassigned";
      if (!data[s]) data[s] = { society: s, customers: 0, billed: 0, collected: 0 };
      data[s].billed += b.netPayable || 0;
      if (b.isPaid) data[s].collected += b.netPayable || 0;
    });
    return Object.values(data).sort((a, b) => b.billed - a.billed);
  }, [customers, filteredBills]);

  // Report 5: Daily Bills
  const dailyBills = useMemo(() => {
    return bills.filter((b) => b.billDate === dailyDate).sort((a, b) => (a.invoiceNo || "").localeCompare(b.invoiceNo || ""));
  }, [bills, dailyDate]);

  // Report 6: Gas Consumption Trend
  const trendData = useMemo(() => {
    const months = {};
    filteredBills.forEach((b) => {
      const d = new Date(b.billDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!months[key]) months[key] = { month: key, units: 0 };
      months[key].units += b.consumedUnits || 0;
    });
    return Object.values(months).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredBills]);

  // Report 7: New vs Existing
  const newVsExistingData = useMemo(() => {
    const months = {};
    customers.forEach((c) => {
      const d = new Date(c.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!months[key]) months[key] = { month: key, newCustomers: 0 };
      months[key].newCustomers++;
    });
    return Object.values(months).sort((a, b) => a.month.localeCompare(b.month));
  }, [customers]);

  // Report 8: Rate Change Impact
  const rateImpactData = useMemo(() => {
    if (!rateChangeDate) return null;
    const rd = new Date(rateChangeDate);
    const before = filteredBills.filter((b) => new Date(b.billDate) < rd);
    const after = filteredBills.filter((b) => new Date(b.billDate) >= rd);
    return {
      beforeAvg: before.length ? before.reduce((s, b) => s + b.netPayable, 0) / before.length : 0,
      afterAvg: after.length ? after.reduce((s, b) => s + b.netPayable, 0) / after.length : 0,
      beforeCount: before.length,
      afterCount: after.length,
      beforeTotal: before.reduce((s, b) => s + b.netPayable, 0),
      afterTotal: after.reduce((s, b) => s + b.netPayable, 0),
    };
  }, [filteredBills, rateChangeDate]);

  const tabs = [
    ["monthly", "Monthly Collection"],
    ["consumption", "Customer Consumption"],
    ["outstanding", "Outstanding Dues"],
    ["society", "Society-wise"],
    ["daily", "Daily Register"],
    ["trend", "Gas Trend"],
    ["newexisting", "New vs Existing"],
    ["ratechange", "Rate Impact"],
  ];

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="reports-page">
      <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900 mb-8">Reports</h1>

      <Tabs defaultValue="monthly">
        <div className="overflow-x-auto mb-6">
          <TabsList className="inline-flex w-auto bg-zinc-100 p-1 rounded-none">
            {tabs.map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-none text-xs whitespace-nowrap px-3 py-1.5">{l}</TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Monthly Collection */}
        <TabsContent value="monthly">
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Monthly Collection Report</CardTitle>
              <ExportButtons
                pdfFn={() => exportPDF("Monthly Collection Report", [
                  { label: "Month", accessor: (r) => r.month },
                  { label: "Billed", accessor: (r) => r.billed.toFixed(2) },
                  { label: "Collected", accessor: (r) => r.collected.toFixed(2) },
                  { label: "Pending", accessor: (r) => r.pending.toFixed(2) },
                ], monthlyData, "monthly_collection")}
                csvFn={() => exportCSV(monthlyData, [
                  { label: "Month", accessor: (r) => r.month },
                  { label: "Billed", accessor: (r) => r.billed.toFixed(2) },
                  { label: "Collected", accessor: (r) => r.collected.toFixed(2) },
                  { label: "Pending", accessor: (r) => r.pending.toFixed(2) },
                ], "monthly_collection")}
              />
            </CardHeader>
            <CardContent>
              <DateFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              <div className="h-[300px] mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717a" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#71717a" }} />
                    <Tooltip contentStyle={chartTooltipStyle} />
                    <Legend />
                    <Bar dataKey="billed" fill="#ea580c" name="Billed" />
                    <Bar dataKey="collected" fill="#10b981" name="Collected" />
                    <Bar dataKey="pending" fill="#ef4444" name="Pending" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-xs font-bold uppercase">Month</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Billed</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Collected</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Pending</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyData.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-zinc-400 py-8">No data for selected range</TableCell></TableRow>
                  ) : monthlyData.map((r) => (
                    <TableRow key={r.month}>
                      <TableCell className="text-sm font-mono">{r.month}</TableCell>
                      <TableCell className="text-sm font-mono text-right">₹{r.billed.toFixed(2)}</TableCell>
                      <TableCell className="text-sm font-mono text-right text-emerald-600">₹{r.collected.toFixed(2)}</TableCell>
                      <TableCell className="text-sm font-mono text-right text-red-600">₹{r.pending.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customer Consumption */}
        <TabsContent value="consumption">
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Customer-wise Consumption</CardTitle>
              <ExportButtons
                pdfFn={() => exportPDF("Customer Consumption", [
                  { label: "Customer", accessor: (r) => r.name },
                  { label: "Consumer No", accessor: (r) => r.consumerNo },
                  { label: "Total Units", accessor: (r) => r.totalUnits.toFixed(2) },
                  { label: "Total Amount", accessor: (r) => r.totalAmount.toFixed(2) },
                ], consumptionData, "customer_consumption")}
                csvFn={() => exportCSV(consumptionData, [
                  { label: "Customer", accessor: (r) => r.name },
                  { label: "Consumer No", accessor: (r) => r.consumerNo },
                  { label: "Total Units", accessor: (r) => r.totalUnits.toFixed(2) },
                  { label: "Total Amount", accessor: (r) => r.totalAmount.toFixed(2) },
                ], "customer_consumption")}
              />
            </CardHeader>
            <CardContent>
              <DateFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-xs font-bold uppercase">Customer</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Consumer No</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Total Units</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Total Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {consumptionData.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-zinc-400 py-8">No data</TableCell></TableRow>
                  ) : consumptionData.map((r) => (
                    <TableRow key={`cons-${r.consumerNo}`}>
                      <TableCell className="text-sm">{r.name}</TableCell>
                      <TableCell className="text-sm font-mono">{r.consumerNo}</TableCell>
                      <TableCell className="text-sm font-mono text-right">{r.totalUnits.toFixed(2)}</TableCell>
                      <TableCell className="text-sm font-mono text-right">₹{r.totalAmount.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outstanding Dues */}
        <TabsContent value="outstanding">
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Outstanding Dues Report</CardTitle>
              <ExportButtons
                pdfFn={() => exportPDF("Outstanding Dues", [
                  { label: "Customer", accessor: (r) => r.customerName },
                  { label: "Invoice No", accessor: (r) => r.invoiceNo },
                  { label: "Due Date", accessor: (r) => new Date(r.dueDate).toLocaleDateString("en-IN") },
                  { label: "Amount", accessor: (r) => r.netPayable?.toFixed(2) },
                  { label: "Days Overdue", accessor: (r) => r.daysOverdue },
                ], outstandingData, "outstanding_dues")}
                csvFn={() => exportCSV(outstandingData, [
                  { label: "Customer", accessor: (r) => r.customerName },
                  { label: "Invoice No", accessor: (r) => r.invoiceNo },
                  { label: "Due Date", accessor: (r) => new Date(r.dueDate).toLocaleDateString("en-IN") },
                  { label: "Amount", accessor: (r) => r.netPayable?.toFixed(2) },
                  { label: "Days Overdue", accessor: (r) => r.daysOverdue },
                ], "outstanding_dues")}
              />
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-xs font-bold uppercase">Customer</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Invoice No</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Due Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Amount</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Days Overdue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outstandingData.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-zinc-400 py-8">No outstanding dues</TableCell></TableRow>
                  ) : outstandingData.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm">{r.customerName}</TableCell>
                      <TableCell className="text-sm font-mono">{r.invoiceNo}</TableCell>
                      <TableCell className="text-sm">{new Date(r.dueDate).toLocaleDateString("en-IN")}</TableCell>
                      <TableCell className="text-sm font-mono text-right text-red-600">₹{r.netPayable?.toFixed(2)}</TableCell>
                      <TableCell className="text-sm font-mono text-right">{r.daysOverdue}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Society-wise */}
        <TabsContent value="society">
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Society-wise Report</CardTitle>
              <ExportButtons
                pdfFn={() => exportPDF("Society-wise Report", [
                  { label: "Society", accessor: (r) => r.society },
                  { label: "Customers", accessor: (r) => r.customers },
                  { label: "Total Billed", accessor: (r) => r.billed.toFixed(2) },
                  { label: "Total Collected", accessor: (r) => r.collected.toFixed(2) },
                ], societyData, "society_report")}
                csvFn={() => exportCSV(societyData, [
                  { label: "Society", accessor: (r) => r.society },
                  { label: "Customers", accessor: (r) => r.customers },
                  { label: "Total Billed", accessor: (r) => r.billed.toFixed(2) },
                  { label: "Total Collected", accessor: (r) => r.collected.toFixed(2) },
                ], "society_report")}
              />
            </CardHeader>
            <CardContent>
              <DateFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-xs font-bold uppercase">Society</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Customers</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Total Billed</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Total Collected</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {societyData.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-zinc-400 py-8">No data</TableCell></TableRow>
                  ) : societyData.map((r) => (
                    <TableRow key={`soc-${r.society}`}>
                      <TableCell className="text-sm">{r.society}</TableCell>
                      <TableCell className="text-sm font-mono text-right">{r.customers}</TableCell>
                      <TableCell className="text-sm font-mono text-right">₹{r.billed.toFixed(2)}</TableCell>
                      <TableCell className="text-sm font-mono text-right text-emerald-600">₹{r.collected.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily Bill Register */}
        <TabsContent value="daily">
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Daily Bill Register</CardTitle>
              <ExportButtons
                pdfFn={() => exportPDF(`Daily Bills - ${dailyDate}`, [
                  { label: "Invoice No", accessor: (r) => r.invoiceNo },
                  { label: "Customer", accessor: (r) => r.customerName },
                  { label: "Units", accessor: (r) => r.consumedUnits },
                  { label: "Amount", accessor: (r) => r.netPayable?.toFixed(2) },
                ], dailyBills, `daily_register_${dailyDate}`)}
                csvFn={() => exportCSV(dailyBills, [
                  { label: "Invoice No", accessor: (r) => r.invoiceNo },
                  { label: "Customer", accessor: (r) => r.customerName },
                  { label: "Units", accessor: (r) => r.consumedUnits },
                  { label: "Amount", accessor: (r) => r.netPayable?.toFixed(2) },
                ], `daily_register_${dailyDate}`)}
              />
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Label className="text-xs">Select Date</Label>
                <Input type="date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1 w-auto" data-testid="daily-date-input" />
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-xs font-bold uppercase">Invoice No</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Customer</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Units</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyBills.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-zinc-400 py-8">No bills on this date</TableCell></TableRow>
                  ) : dailyBills.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm font-mono">{r.invoiceNo}</TableCell>
                      <TableCell className="text-sm">{r.customerName}</TableCell>
                      <TableCell className="text-sm font-mono text-right">{r.consumedUnits}</TableCell>
                      <TableCell className="text-sm font-mono text-right">₹{r.netPayable?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {dailyBills.length > 0 && (
                <div className="mt-4 text-right text-sm font-semibold border-t pt-3">
                  Total: <span className="font-mono text-orange-600">₹{dailyBills.reduce((s, b) => s + (b.netPayable || 0), 0).toFixed(2)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gas Consumption Trend */}
        <TabsContent value="trend">
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Gas Consumption Trend</CardTitle>
              <ExportButtons
                pdfFn={() => exportPDF("Gas Consumption Trend", [
                  { label: "Month", accessor: (r) => r.month },
                  { label: "Total Units", accessor: (r) => r.units.toFixed(2) },
                ], trendData, "gas_consumption_trend")}
                csvFn={() => exportCSV(trendData, [
                  { label: "Month", accessor: (r) => r.month },
                  { label: "Total Units", accessor: (r) => r.units.toFixed(2) },
                ], "gas_consumption_trend")}
              />
            </CardHeader>
            <CardContent>
              <DateFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717a" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#71717a" }} />
                    <Tooltip contentStyle={chartTooltipStyle} />
                    <Line type="monotone" dataKey="units" stroke="#ea580c" strokeWidth={2} dot={{ fill: "#ea580c", r: 4 }} name="Total Units" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* New vs Existing */}
        <TabsContent value="newexisting">
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">New vs Existing Customers</CardTitle>
              <ExportButtons
                pdfFn={() => exportPDF("New Customers per Month", [
                  { label: "Month", accessor: (r) => r.month },
                  { label: "New Customers", accessor: (r) => r.newCustomers },
                ], newVsExistingData, "new_vs_existing")}
                csvFn={() => exportCSV(newVsExistingData, [
                  { label: "Month", accessor: (r) => r.month },
                  { label: "New Customers", accessor: (r) => r.newCustomers },
                ], "new_vs_existing")}
              />
            </CardHeader>
            <CardContent>
              <div className="h-[300px] mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={newVsExistingData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717a" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#71717a" }} allowDecimals={false} />
                    <Tooltip contentStyle={chartTooltipStyle} />
                    <Bar dataKey="newCustomers" fill="#ea580c" name="New Customers" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-zinc-500">Total customers: <span className="font-semibold text-zinc-900">{customers.length}</span></p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rate Change Impact */}
        <TabsContent value="ratechange">
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader>
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Rate Change Impact</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <Label className="text-xs">Rate Change Date</Label>
                <Input type="date" value={rateChangeDate} onChange={(e) => setRateChangeDate(e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1 w-auto" data-testid="rate-change-date-input" />
              </div>
              {rateImpactData ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 bg-zinc-50 border border-zinc-200">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Before Rate Change</p>
                    <div className="space-y-2 text-sm">
                      <p>Bills: <span className="font-mono font-semibold">{rateImpactData.beforeCount}</span></p>
                      <p>Total Amount: <span className="font-mono font-semibold">₹{rateImpactData.beforeTotal?.toFixed(2)}</span></p>
                      <p>Avg Bill: <span className="font-mono font-semibold">₹{rateImpactData.beforeAvg?.toFixed(2)}</span></p>
                    </div>
                  </div>
                  <div className="p-6 bg-orange-50 border border-orange-200">
                    <p className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-4">After Rate Change</p>
                    <div className="space-y-2 text-sm">
                      <p>Bills: <span className="font-mono font-semibold">{rateImpactData.afterCount}</span></p>
                      <p>Total Amount: <span className="font-mono font-semibold">₹{rateImpactData.afterTotal?.toFixed(2)}</span></p>
                      <p>Avg Bill: <span className="font-mono font-semibold">₹{rateImpactData.afterAvg?.toFixed(2)}</span></p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-zinc-400 py-8 text-center">Select a rate change date to compare billing amounts.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
